import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  FileText, Plus, Edit, Trash2, Eye, Loader2, ArrowLeft, Save, Upload, Code, Type,
  Calendar, Tag, Search as SearchIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";

const STATUS_MAP = {
  borrador: { label: "Borrador", color: "bg-gray-500" },
  programado: { label: "Programado", color: "bg-yellow-500" },
  publicado: { label: "Publicado", color: "bg-green-500" },
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link", "image"],
    ["clean"],
  ],
};

function PostList({ posts, onEdit, onDelete, onNew }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    if (!search) return posts;
    const t = search.toLowerCase();
    return posts.filter(p => p.title.toLowerCase().includes(t) || p.category?.toLowerCase().includes(t));
  }, [posts, search]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Posts del blog</h2>
        <Button onClick={onNew} className="btn-primary text-sm" data-testid="new-post-btn">
          <Plus className="w-4 h-4" /> Nuevo post
        </Button>
      </div>
      <div className="relative mb-4 max-w-md">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar posts..." className="pl-10 bg-[#1A1F2E] border-gray-700 text-white" />
      </div>
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No hay posts. Crea el primero.</p>
          </div>
        ) : filtered.map(post => (
          <div key={post.id} className="bg-[#1A1F2E] border border-gray-700/50 rounded-lg p-4 flex items-center gap-4" data-testid={`post-item-${post.id}`}>
            {post.image && <img src={post.image} alt="" className="w-16 h-10 object-cover rounded" />}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{post.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`w-2 h-2 rounded-full ${STATUS_MAP[post.status]?.color || "bg-gray-500"}`}></span>
                <span className="text-gray-500 text-xs">{STATUS_MAP[post.status]?.label || post.status}</span>
                {post.category && <span className="text-xs px-2 py-0.5 rounded bg-blue-500/15 text-blue-400">{post.category}</span>}
                <span className="text-gray-600 text-xs">{new Date(post.publish_date || post.created_at).toLocaleDateString("es-ES")}</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-blue-400 transition-colors" title="Ver">
                <Eye className="w-4 h-4" />
              </a>
              <button onClick={() => onEdit(post)} className="p-2 text-gray-400 hover:text-white transition-colors" title="Editar" data-testid={`edit-post-${post.id}`}>
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => onDelete(post.id)} className="p-2 text-gray-400 hover:text-red-400 transition-colors" title="Eliminar" data-testid={`delete-post-${post.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostEditor({ post, onSave, onCancel }) {
  const [title, setTitle] = useState(post?.title || "");
  const [subtitle, setSubtitle] = useState(post?.subtitle || "");
  const [category, setCategory] = useState(post?.category || "");
  const [metaDesc, setMetaDesc] = useState(post?.meta_description || "");
  const [keywords, setKeywords] = useState(post?.keywords?.join(", ") || "");
  const [content, setContent] = useState(post?.content || "");
  const [status, setStatus] = useState(post?.status || "borrador");
  const [publishDate, setPublishDate] = useState(post?.publish_date ? post.publish_date.slice(0, 16) : "");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(post?.image || "");
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState("visual");

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("subtitle", subtitle);
      formData.append("category", category);
      formData.append("meta_description", metaDesc);
      formData.append("keywords", keywords);
      formData.append("content", content);
      formData.append("status", status);
      formData.append("publish_date", publishDate ? new Date(publishDate).toISOString() : "");
      if (imageFile) formData.append("image", imageFile);

      let res;
      if (post?.id) {
        res = await axios.put(`${API}/admin/blog/${post.id}`, formData, { withCredentials: true });
      } else {
        res = await axios.post(`${API}/admin/blog`, formData, { withCredentials: true });
      }
      toast.success(post?.id ? "Post actualizado" : "Post creado");
      onSave(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button onClick={onCancel} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Volver a la lista
        </button>
        <div className="flex items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 bg-[#1A1F2E] border-gray-700 text-white" data-testid="post-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="borrador">Borrador</SelectItem>
              <SelectItem value="programado">Programado</SelectItem>
              <SelectItem value="publicado">Publicado</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSubmit} disabled={saving} className="btn-primary text-sm" data-testid="save-post-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {post?.id ? "Actualizar" : "Crear"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="text-gray-400 text-sm mb-1.5 block">Título *</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título del post" className="bg-[#1A1F2E] border-gray-700 text-white text-lg" data-testid="post-title" />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1.5 block">Subtítulo</label>
            <Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Subtítulo descriptivo" className="bg-[#1A1F2E] border-gray-700 text-white" data-testid="post-subtitle" />
          </div>

          {/* Editor toggle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-400 text-sm">Contenido</label>
              <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-0.5">
                <button onClick={() => setEditorMode("visual")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${editorMode === "visual" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-white"}`} data-testid="mode-visual">
                  <Type className="w-3.5 h-3.5" /> Visual
                </button>
                <button onClick={() => setEditorMode("html")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${editorMode === "html" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-white"}`} data-testid="mode-html">
                  <Code className="w-3.5 h-3.5" /> HTML
                </button>
              </div>
            </div>

            {editorMode === "visual" ? (
              <div className="blog-editor-wrapper" data-testid="visual-editor">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={QUILL_MODULES}
                  placeholder="Escribe el contenido del post..."
                />
              </div>
            ) : (
              <Textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="<h2>Título</h2>\n<p>Contenido HTML...</p>"
                className="bg-[#1A1F2E] border-gray-700 text-gray-300 font-mono text-sm min-h-[400px]"
                data-testid="html-editor"
              />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Featured image */}
          <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-4">
            <label className="text-gray-400 text-sm mb-3 block flex items-center gap-2">
              <Upload className="w-4 h-4" /> Imagen destacada
            </label>
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg mb-3" data-testid="image-preview" />
            )}
            <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-500/15 file:text-blue-400 hover:file:bg-blue-500/25 file:cursor-pointer" data-testid="image-upload" />
            <p className="text-gray-600 text-xs mt-2">Se optimiza automáticamente para web y redes sociales (1200x630 OG)</p>
          </div>

          {/* Publish date */}
          <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-4">
            <label className="text-gray-400 text-sm mb-2 block flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Fecha de publicación
            </label>
            <Input type="datetime-local" value={publishDate} onChange={e => setPublishDate(e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="publish-date" />
            <p className="text-gray-600 text-xs mt-2">Si pones fecha futura y estado «Programado», se publicará automáticamente</p>
          </div>

          {/* SEO */}
          <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-4 space-y-3">
            <label className="text-gray-400 text-sm flex items-center gap-2">
              <Tag className="w-4 h-4" /> SEO y metadatos
            </label>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Categoría</label>
              <Input value={category} onChange={e => setCategory(e.target.value)} placeholder="AI Act, RGPD, AESIA..." className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="post-category" />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Meta descripción</label>
              <Textarea value={metaDesc} onChange={e => setMetaDesc(e.target.value)} placeholder="Descripción para buscadores (max 160 caracteres)" className="bg-gray-800 border-gray-700 text-white text-sm min-h-[70px]" data-testid="post-meta-desc" />
              <p className="text-gray-600 text-xs mt-1">{metaDesc.length}/160</p>
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Etiquetas (separadas por comas)</label>
              <Input value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="AI Act, RRHH, clínica estética" className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="post-keywords" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlog() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(res.data);
    } catch (err) { toast.error("Error al cargar posts"); }
  };

  useEffect(() => { if (user) fetchPosts(); }, [user]);

  const handleNew = () => { setEditing(null); setShowEditor(true); };
  const handleEdit = (post) => { setEditing(post); setShowEditor(true); };
  const handleSave = () => { setShowEditor(false); setEditing(null); fetchPosts(); };
  const handleCancel = () => { setShowEditor(false); setEditing(null); };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { withCredentials: true });
      toast.success("Post eliminado");
      fetchPosts();
    } catch (err) { toast.error("Error al eliminar"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>;

  return showEditor
    ? <PostEditor post={editing} onSave={handleSave} onCancel={handleCancel} />
    : <PostList posts={posts} onEdit={handleEdit} onDelete={handleDelete} onNew={handleNew} />;
}
